import { useMemo, useState } from 'react';
import {
  TeButton,
  TeButtonMinimal,
  TeContainerSlim,
  TeContextMenu,
  TeContextMenuControl,
  TeContextMenuItem,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';

import PageLayout from '../../../components/Page/PageLayout';
import EmptyState from '../../../components/ServiceAgreement/Common/EmptyState';
import TerminateCancelServiceAgreementModal from '../../../components/ServiceAgreement/Customer/TerminateCancelServiceAgreementModal';
import {
  ReviewSource,
  navigateToReviewPage,
} from '../../../components/ServiceAgreement/serviceAgreementReviewNavigation';
import {
  ServiceAgreementExpiredEnum,
  type ServiceAgreement,
} from '../../../components/ServiceAgreement/Common/ServiceAgreementTypes';
import { mapServiceAgreementBody } from '../../../utils/serviceAgreement';
import AddressCell from '../common/cells/AddressCell';
import BusinessPartnerIconCell from '../common/cells/BusinessPartnerIconCell';
import CellText from '../common/cells/CellText';
import CountryCell from '../common/cells/CountryCell';
import DivisionCell from '../common/cells/DivisionCell';
import ExpiresInCell from '../common/cells/ExpiresInCell';
import OrganizationNumberCell from '../common/cells/OrganizationNumberCell';
import ServiceTypeCell from '../common/cells/ServiceTypeCell';
import StatusBadgeCell from '../common/cells/StatusBadgeCell';
import DivisionFilterButton from '../common/DivisionFilterButton';
import DivisionFilterDrawer from '../common/DivisionFilterDrawer';
import DivisionQuickFilter from '../common/DivisionQuickFilter';
import ServerPagedTable, { type ServerPagedTableColumn } from '../common/ServerPagedTable';
import { useDivisionRollup } from '../../../hooks/queries/useDivisionRollup';
import { useListError } from '../../../hooks/useListError';
import { useListState } from '../../../hooks/useListState';
import { useVisibilityRefetch } from '../../../hooks/useVisibilityRefetch';
import { useInvitesList } from './invites.queries';

function CustomerInvites() {
  const { t } = useTranslation();
  const divisionRollup = useDivisionRollup();
  const { page, setPage, limit, setLimit, searchInput, setSearchInput, search } = useListState({
    resetKey: divisionRollup.filterKey,
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<ServiceAgreement>();

  const query = useInvitesList({
    page,
    limit,
    search,
    scope: divisionRollup.scope,
    selectedDivisionIds: divisionRollup.selectedDivisionIds,
    enabled: divisionRollup.isReady,
  });

  useVisibilityRefetch(query.refetch);
  const isError = useListError(query.error);

  const items = useMemo<ServiceAgreement[]>(
    () => (query.data?.items ?? []).map(mapServiceAgreementBody),
    [query.data]
  );

  const renderActions = (sa: ServiceAgreement) => {
    if (!divisionRollup.isOwnRow(sa.providerBusinessPartnerId)) return null;

    if (sa.computedServiceAgreementStatus === ServiceAgreementExpiredEnum.Expired) {
      return (
        <>
          <TeContextMenuControl contextId={`invite-actions-${sa.id}`}>
            <TeButtonMinimal icon="more_vert" variant="secondary" size="s" />
          </TeContextMenuControl>
          <TeContextMenu contextId={`invite-actions-${sa.id}`} size="s">
            <TeContextMenuItem
              text={t('cancelAgreementModal.title')}
              textColor="text.error.primary"
              teclick={() => setCancelTarget(sa)}
            />
          </TeContextMenu>
        </>
      );
    }

    return (
      <TeButton
        variant="neutral"
        text={t('review')}
        size="s"
        teclick={() => navigateToReviewPage(sa.id, ReviewSource.Customers)}
      />
    );
  };

  const columns = useMemo<ServerPagedTableColumn<ServiceAgreement>[]>(() => {
    const cols: ServerPagedTableColumn<ServiceAgreement>[] = [
      {
        key: 'icon',
        header: '',
        compact: true,
        render: () => <BusinessPartnerIconCell />,
      },
      {
        key: 'companyName',
        header: t('customersTable.columnCompanyName'),
        minWidth: 200,
        render: (sa) => <CellText text={sa.customer?.name ?? ''} />,
      },
    ];

    if (divisionRollup.hasDivisions) {
      cols.push({
        key: 'division',
        header: t('customersTable.columnDivision'),
        minWidth: 160,
        render: (sa) => (
          <DivisionCell name={divisionRollup.resolveDivisionName(sa.providerBusinessPartnerId)} />
        ),
      });
    }

    cols.push(
      {
        key: 'serviceType',
        header: t('customersTable.columnServiceType'),
        render: (sa) => <ServiceTypeCell agreementTerms={sa.serviceAgreementTerms} />,
      },
      {
        key: 'expiresIn',
        header: t('customersTable.columnExpiresIn'),
        justifyContent: 'flex-end',
        render: (sa) =>
          sa.computedServiceAgreementStatus === ServiceAgreementExpiredEnum.Expired ? (
            <TeContainerSlim gap="xs" alignItems="center">
              <StatusBadgeCell status={sa.computedServiceAgreementStatus} />
              <ExpiresInCell requestExpiresIn={sa.requestExpiresIn} />
            </TeContainerSlim>
          ) : (
            <ExpiresInCell requestExpiresIn={sa.requestExpiresIn} />
          ),
      },
      {
        key: 'organizationNumber',
        header: t('customersTable.columnOrganizationNumber'),
        headerInfoTooltip: t('customersTable.tooltipOrganizationNumber'),
        justifyContent: 'flex-end',
        render: (sa) => <OrganizationNumberCell extId={sa.customer?.extId} />,
      },
      {
        key: 'companyAddress',
        header: t('customersTable.columnCompanyAddress'),
        minWidth: 240,
        render: (sa) => <AddressCell address={sa.customer?.companyAddress} />,
      },
      {
        key: 'country',
        header: t('customersTable.columnCountry'),
        minWidth: 120,
        render: (sa) => <CountryCell countryCode={sa.customer?.companyAddress.country} />,
      },
      {
        key: 'actions',
        header: '',
        compact: true,
        justifyContent: 'flex-end',
        render: renderActions,
      }
    );

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, divisionRollup.hasDivisions, divisionRollup.resolveDivisionName, divisionRollup.isOwnRow]);

  const hasActiveDivisionFilter = divisionRollup.selectedDivisionIds.length > 0;

  return (
    <PageLayout
      fullWidth
      title={t('customerInvites', { ns: 'navigation' })}
      itemCount={query.isError ? undefined : query.data?.total}
      leftAccessory={
        divisionRollup.hasDivisions ? (
          <DivisionQuickFilter scope={divisionRollup.scope} onChange={divisionRollup.setScope} />
        ) : undefined
      }
      searchValue={searchInput}
      onSearchChange={setSearchInput}
      searchPlaceholder={t('invitesTable.searchPlaceholder')}
      actions={
        divisionRollup.hasDivisions ? (
          <DivisionFilterButton
            isOpen={filtersOpen}
            hasActiveFilter={hasActiveDivisionFilter}
            onClick={() => setFiltersOpen((open) => !open)}
          />
        ) : undefined
      }
      drawer={
        divisionRollup.hasDivisions ? (
          <DivisionFilterDrawer
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            divisions={divisionRollup.divisions}
            selectedDivisionIds={divisionRollup.selectedDivisionIds}
            onApply={divisionRollup.applyDivisions}
            onClear={divisionRollup.clearDivisions}
          />
        ) : undefined
      }
    >
      <ServerPagedTable
        columns={columns}
        items={items}
        rowKey={(sa) => sa.id}
        total={query.data?.total ?? 0}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={setLimit}
        // Disabled until Divisions settle; see Customers.tsx.
        isLoading={query.isPending}
        isFetching={query.isFetching}
        isError={isError}
        emptyState={
          <EmptyState
            kind={search ? 'noSearch' : 'noItems'}
            resource="customers"
            subset="invites"
          />
        }
      />

      {cancelTarget && (
        <TerminateCancelServiceAgreementModal
          serviceAgreement={cancelTarget}
          kind="agreement"
          open={!!cancelTarget}
          onClose={() => setCancelTarget(undefined)}
        />
      )}
    </PageLayout>
  );
}

export default CustomerInvites;
